import { css, SerializedStyles } from '@emotion/core'
import React, { ReactNode } from 'react'

import { breakpoints } from '../constants/breakPoints'
import { InstantTourType, useInstantTourLinkFactory, useSignUpLink } from '../hooks/useTracking'
import { useTranslation } from '../lang'
import { Link } from './Link'

export type ButtonType = 'primary' | 'secondary' | 'ghost' | 'borderless' | 'special'
export interface ButtonTrackingData {
  type: string
}
interface Props {
  type?: ButtonType
  children: ReactNode
  size?: 'big' | 'normal'
  link?: string
  onClick?: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void
  className?: string
  cssStyles?: SerializedStyles
  tracking?: ButtonTrackingData
}

export const Button: React.FunctionComponent<Props> = ({
  type = 'primary',
  size = 'normal',
  link = '',
  onClick,
  className,
  cssStyles,
  tracking,
  children,
}) => {
  const style = [
    buttonStyle,
    size === 'big' && buttonBigStyle,
    type === 'primary' && buttonPrimaryStyle,
    type === 'borderless' && buttonBorderlessStyle,
    type === 'ghost' && buttonGhostStyle,
    type === 'special' && buttonSpecialStyle,
    type === 'secondary' && buttonSecondaryStyle,
  ]
  if (link) {
    return (
      <Link
        href={link}
        onClick={onClick}
        target={'_blank'}
        css={[style, cssStyles]}
        className={className}
        data-tracking-type={tracking && tracking.type}
      >
        {children}
      </Link>
    )
  } else {
    return (
      <div onClick={onClick} css={[style, cssStyles]} className={className}>
        {children}
      </div>
    )
  }
}

export const buttonStyle = css`
  -webkit-appearance: none;
  display: inline-block;
  text-decoration: none;
  text-align: center;
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  letter-spacing: 0.13px;
  max-width: 250px;
  cursor: pointer;
  box-sizing: border-box;
  outline: none;
  transition: all 0.5s ease;
  user-select: none;
  p {
    text-align: center;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    max-width: 100%;
    width: 100%;
  }
`

const buttonBigStyle = css`
  padding: 15px 25px;
`

export const buttonPrimaryStyle = css`
  padding: 0.75rem 1.5rem;
  background: #318eff;

  &:hover {
    background: #3146ff;
  }
`

const buttonGhostStyle = css`
  border: 1px solid #318eff;
  color: #318eff;
  padding-top: 10px;
  padding-bottom: 10px;

  &:hover {
    color: #3146ff;
    border: 1px solid #3146ff;
  }

  &.black {
    border: 2px solid #000;
    color: #000;
    border-color: #000;

    &:hover {
      color: #fff;
      background-color: #000;
    }
  }

  &.white {
    color: #fff;
    border-color: rgba(255, 255, 255, 0.1);
    background-color: rgba(255, 255, 255, 0.1);

    &:hover {
      background-color: rgba(255, 255, 255, 0.2);
    }
  }
`

const buttonSecondaryStyle = css`
  background-color: #ffffff;
  border: solid 1px #e7edf2;
  color: #42aef0;
`

const buttonBorderlessStyle = css`
  padding: 0;
  color: #42aef0;
  &:hover {
    text-decoration: none;
    color: #0666c6;
  }
  text-align: left;
  max-width: initial;
`

const buttonSpecialStyle = css`
  padding: 0.75rem 1.5rem;
  border-radius: 0.25rem;
  background-color: #e3f5ff;
  color: #318eff;
  &:hover {
    background-color: #eef9ff;
    text-decoration: underline;
  }
`

interface TrialButtonProps {
  type?: ButtonType
  className?: string
  buttonText?: string
  cssStyles?: SerializedStyles
}

export const TrialButton: React.FunctionComponent<TrialButtonProps> = ({
  type = 'primary',
  buttonText,
  className = `trial-button ${type === 'ghost' ? 'black' : ''}`,
  cssStyles = css``,
}) => {
  const { t } = useTranslation()
  return (
    <Button
      type={type}
      className={className}
      link={useSignUpLink()}
      tracking={{ type: 'start-free-trial-button' }}
      cssStyles={cssStyles}
    >
      <p className={'strong-body-2'}>{buttonText ?? t('trialText')}</p>
    </Button>
  )
}

interface DemoButtonProps {
  type?: ButtonType
  className?: string
  buttonText?: string
  cssStyles?: SerializedStyles
}

export const DemoButton: React.FunctionComponent<DemoButtonProps> = ({
  type = 'primary',
  buttonText,
  className = `demo-button ${type === 'ghost' ? 'black' : ''}`,
  cssStyles = css``,
}) => {
  const { t } = useTranslation()
  return (
    <Button type={type} className={className} link={t('demoLink')} cssStyles={cssStyles}>
      <p className={'strong-body-2'}>{buttonText ?? t('demoText')}</p>
    </Button>
  )
}

interface TourButtonProps {
  type?: ButtonType
  buttonText?: string
  instantTourType?: InstantTourType
  className?: string
  cssStyles?: SerializedStyles
}

export const TourButton: React.FunctionComponent<TourButtonProps> = ({
  type = 'ghost',
  buttonText,
  instantTourType,
  className = `tour-button ${type === 'ghost' ? 'black' : ''}`,
  cssStyles = css``,
}) => {
  const { t } = useTranslation()
  return (
    <Button type={type} className={className} link={useInstantTourLinkFactory()(instantTourType)} cssStyles={cssStyles}>
      <p className={'strong-body-2'}>{buttonText ?? t('tourText')}</p>
    </Button>
  )
}
